/**
 * Set up language picker.
 *
 * @returns {void}
 */
export default () => {

	const button = document.getElementById( 'language-picker-button' );
	const form = document.getElementById( 'language-picker' );
	const input = document.getElementById( 'language-picker-input' );
	const menu = document.getElementById( 'language-picker-menu' );

	if ( ! button ) {
		return;
	}

	/**
	 * Close the menu.
	 *
	 * @returns {void}
	 */
	const close = () => {
		button.setAttribute( 'aria-expanded', 'false' );
		menu.setAttribute( 'aria-hidden', 'true' );
	};

	/**
	 * Open the menu.
	 *
	 * @returns {void}
	 */
	const open = () => {
		button.setAttribute( 'aria-expanded', 'true' );
		menu.setAttribute( 'aria-hidden', 'false' );
	};

	/**
	 * Select a menu item.
	 *
	 * @returns {void}
	 */
	const select = () => {
		const el = document.activeElement;
		if ( el && menu.contains( el ) && el.dataset.value ) {
			input.value = el.dataset.value;
			form.submit();
		}
	};

	/**
	 * Focus on the button.
	 *
	 * @returns {void}
	 */
	const toButton = () => {
		button.focus();
	};

	/**
	 * Focus on the first menu item.
	 *
	 * @returns {void}
	 */
	const toFirst = () => {
		menu.firstElementChild.focus();
	};

	/**
	 * Focus on the last menu item.
	 *
	 * @returns {void}
	 */
	const toLast = () => {
		menu.lastElementChild.focus();
	};

	/**
	 * Focus on the next menu item.
	 *
	 * @returns {void}
	 */
	const toNext = () => {
		const { parentElement, nextElementSibling } = document.activeElement;

		if ( nextElementSibling ) {
			nextElementSibling.focus();
		} else {
			parentElement.firstElementChild.focus();
		}
	};

	/**
	 * Focus on the pervious menu item.
	 *
	 * @returns {void}
	 */
	const toPrevious = () => {
		const { parentElement, previousElementSibling } = document.activeElement;

		if ( previousElementSibling ) {
			previousElementSibling.focus();
		} else {
			parentElement.lastElementChild.focus();
		}
	};

	/**
	 * Handle clicks on the button.
	 *
	 * @param {PointerEvent} event Pointer event.
	 * @returns {void}
	 */
	const onButtonClick = ( event ) => {
		const isOpen = event.currentTarget.getAttribute( 'aria-expanded' ) === 'true';

		if ( isOpen ) {
			close();
			toButton();
		} else {
			open();
			toFirst();
		}
	};

	/**
	 * Handle key presses on the button.
	 *
	 * @param {KeyboardEvent} event Keyboard event.
	 * @returns {void}
	 */
	const onButtonKeyDown = ( event ) => {
		const preventKeys = [ 'ArrowDown', 'ArrowUp' ];

		if ( preventKeys.includes( event.key ) ) {
			event.preventDefault();
		}

		if ( event.key === 'ArrowDown' ) {
			open();
			toFirst();
		}

		if ( event.key === 'ArrowUp' ) {
			open();
			toLast();
		}

		if ( event.key === 'Escape' ) {
			close();
		}
	};

	/**
	 * Handle focus leaving the menu.
	 *
	 * @param {FocusEvent} event Keyboard event.
	 * @returns {void}
	 */
	const onFormFocusOut = ( event ) => {
		const isLeaving = !event.currentTarget.contains( event.relatedTarget );

		if ( isLeaving ) {
			close();
		}
	};

	/**
	 * Handle clicks in the menu.
	 *
	 * @param {PointerEvent} event Pointer event.
	 * @returns {void}
	 */
	const onMenuClick = ( event ) => {
		const { value } = event.target.dataset;

		if ( value ) {
			select( value );
		}
	};

	/**
	 * Handle key presses in the menu.
	 *
	 * @param {KeyboardEvent} event Keyboard event.
	 * @returns {void}
	 */
	const onMenuKeyDown = ( event ) => {
		const preventKeys = [ 'ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' ' ];

		if ( preventKeys.includes( event.key ) ) {
			event.preventDefault();
		}

		if ( event.key === 'Enter' || event.key === ' ' ) {
			select( document.activeElement.dataset.value );
		}

		if ( event.key === 'ArrowDown' ) {
			toNext();
		}

		if ( event.key === 'ArrowUp' ) {
			toPrevious();
		}

		if ( event.key === 'Home' ) {
			toFirst();
		}

		if ( event.key === 'End' ) {
			toLast();
		}

		if ( event.key === 'Escape' ) {
			close();
			toButton();
		}
	};

	/**
	 * Handle mouseover events in the menu.
	 *
	 * @param {MouseEvent} event Mouse event.
	 * @returns {void}
	 */
	const onMenuMouseOver = ( event ) => {
		if ( event.target.getAttribute( 'role' ) === 'menuitemradio' ) {
			event.target.focus();
		}
	};

	/**
	 * Bind events.
	 */
	button.addEventListener( 'keydown', onButtonKeyDown );
	button.addEventListener( 'click', onButtonClick );
	form.addEventListener( 'focusout', onFormFocusOut );
	menu.addEventListener( 'click', onMenuClick );
	menu.addEventListener( 'keydown', onMenuKeyDown );
	menu.addEventListener( 'mouseover', onMenuMouseOver );
};
